import {
  Component,
  ElementRef,
  ViewChild,
  EventEmitter,
  OnDestroy,
  ChangeDetectorRef,
  computed,
  inject,
  signal,
  effect,
  model,
  AfterViewInit,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { APIService } from 'src/app/services/APIService/api.service';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import * as Plotly from 'plotly.js-basic-dist-min';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { Options } from '@angular-slider/ngx-slider';

import { ClarityModule } from '@clr/angular';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SpinnerService } from 'src/app/services/Spinner/spinner.service';
import { ExcelService } from 'src/app/services/Shared/excel.service';
import { PlotlyPlotComponent } from '../../plotly-plot/plotly-plot.component';

var holdconfig = {
  isDown: 0,
  delay: 50,
  nextTime: 0,
};

interface FilterCoeffs {
  a: number[];
  b: number[];
}

export interface ProfileResponse {
  data: ProfileListItems[];
  sollprofile: SollProfile[];
}

export interface ProfileListItems {
  id: number;
  datum: number;
  messpunkte: MesspunktHeader[];
}

export interface MesspunktHeader {
  zIndex: number;
  Position: number;
  File: string;
  ProfilL: string;
  ProfilR: string;
}

export interface SollProfile {
  name: string;
  IstL: Point[];
  IstR: Point[];
}

export interface Profil {
  railTyp: number;
  zIndex: number;
  position: number;
  dS1L: string;
  dS2L: string;
  dhL: string;
  dS1R: string;
  dS2R: string;
  dhR: string;
  istL: Point[];
  istR: Point[];
}

type ResultItem = {
  datum: string;
  id: number;
};

@Component({
  selector: 'app-profileviewer',
  templateUrl: './profileviewer.component.html',
  styleUrls: ['./profileviewer.component.scss'],
  imports: [
    ClarityModule,
    MrTranslatePipe,
    NgxSliderModule,
    MrTranslatePipe,
    FormsModule,
    ReactiveFormsModule,
    PlotlyPlotComponent,
  ],
})
export class ProfileViewerComponent implements OnDestroy, AfterViewInit {
  private apiservice = inject(APIService);
  private toastr = inject(ToastrService);
  private mrTranslate = inject(MrTranslatePipe);
  private spinner = inject(SpinnerService);
  private change = inject(ChangeDetectorRef);
  private excelService = inject(ExcelService);

  readonly stammid = model<number>(0, { alias: 'setostammid' });
  readonly rechte = model<number>(1, { alias: 'setrechte' });
  private readonly apiService = inject(APIService);
  readonly selections = signal<ResultItem[]>([]);
  readonly selection_ = signal<ResultItem[]>([]);

  private readonly profilesResource = rxResource<ProfileResponse, number>({
    params: this.stammid,
    stream: ({ params: ostammid }) => this.apiService.getProfileList(ostammid),
  });

  protected readonly profileframes = computed<ProfileListItems[]>(() => {
    const value = this.profilesResource.value();
    return (value?.data ?? []).slice().sort((a, b) => Number(b.id) - Number(a.id));
  });

  protected readonly sollprofile = computed(() => this.profilesResource.value()?.sollprofile ?? []);

  protected readonly imagesLoading = this.profilesResource.isLoading;
  protected currentIndex = signal(0);
  protected currentProfile = signal('');
  protected currentPosition = signal<number>(0);
  protected readonly selectedIds = computed(() =>
    this.selection_()
      .map(x => x.id)
      .join(',')
  );

  constructor() {
    // Auto-load first frame when data arrives
    effect(() => {
      const profileframes = this.profileframes();

      if (profileframes.length === 0) return;

      if (this.selections()?.length === 0 && profileframes.length > 0) {
        const mapped = profileframes.map(item => ({
          datum: String(item.datum),
          id: item.id,
        }));

        this.selections.set(mapped);

        this.selection_.set([mapped[0]]);
      }

      this.slideroptions = {
        floor: 0,
        translate: this.getPosition,
        ceil: profileframes[0].messpunkte.length - 1,
        animate: false,
      };

      // only initialize once
      if (this.currentIndex() === 0 && !this.currentProfile()) {
        this.setProfile(0);
      }
    });
  }

  curSoll: any = [];
  hp01: Point | undefined;
  hp02: Point | undefined;
  hp0: Point | null | undefined;
  sp01: Point | null | undefined;
  sp02: Point | null | undefined;
  spSoll: Point | null | undefined;
  spIst: Point | null | undefined;
  sp01L: Point | null | undefined;
  sp02L: Point | null | undefined;
  spSollL: Point | null | undefined;
  spIstL: Point | null | undefined;
  ist: any = [];

  public sizew: number = 700;
  public sizeh: number = 600;

  objektname: string = '';
  isLoading: boolean = false;
  public profiles: any = [];
  private sollProfiles: any = {};
  public index: number = 0;
  tblPruefids: any;
  selDate: any;
  selection: any;
  selpruefids: string = '';
  selDates: string[] = [];
  selIsOpen = false;
  private startpos: number = 0;
  @ViewChild('myCanvasL', { static: true }) canvasRefL: ElementRef | undefined;
  @ViewChild('myCanvasR', { static: true }) canvasRefR: ElementRef | undefined;

  public GLOB_SCALE: number = 1;
  private ctxL: CanvasRenderingContext2D | undefined;
  private ctxR: CanvasRenderingContext2D | undefined;
  private moveXL: number = 0;
  private moveYL: number = 0;
  private moveXR: number = 0;
  private moveYR: number = 0;
  public pr = { l: '', r: '', weg: 0 };
  public ds = { l1: '0', l2: '0', lh: '0', r1: '0', r2: '0', rh: '0' };
  public xy = { x: 0.0, y: 0.0, x2: 0.0, y2: 0.0 };
  public moveStepL: number = 1;
  public moveStepR: number = 1;
  public indexScrub: BehaviorSubject<number> = new BehaviorSubject(0);
  public closedialog: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private manualRefresh: EventEmitter<void> = new EventEmitter<void>();
  showmesswerte: boolean = false;
  exportData: { lX: number; lY: number; rX: number; rY: number }[] = [];
  showdimensioning = false;

  public slideroptions: Options = {
    floor: 0,
    ceil: 100,
  };

  @ViewChild('wearPlot') wearPlot!: ElementRef;
  @ViewChild('plotDiv') plotDiv!: ElementRef<HTMLDivElement>;
  showWearDia: boolean = false;

  graphWear: any = {
    layout: {
      autosize: true,
      hovermode: 'closest',
      showlegend: true,
      margin: { l: 60, r: 20, b: 20, t: 20, pad: 0 },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
    },
    style: {
      width: '100%',
      height: '500px',
    },
    data: [],
  };

  graphL = {
    show: true,
    id: 'plotly_Left',
    layout: {
      autosize: false,
      showlegend: false,
      height: 590,
      margin: {
        l: 20,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: {
        //autorange: true,
        range: [-40, 100], //[Math.min(...measurements) - 10, Math.max(...measurements) + 10],
        //type: "linear",
      },
      xaxis: {
        //autorange: true,
        range: [-90, 50],
      },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: ['sendDataToCloud', 'lasso2d'],
    },
    data: [],
  };

  graphR = {
    id: 'plotly_Right',
    layout: {
      autosize: false,
      showlegend: false,
      height: 590,
      margin: {
        l: 20,
        r: 20,
        b: 20,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: {
        //autorange: true,
        range: [-40, 100], //[Math.min(...measurements) - 10, Math.max(...measurements) + 10],
        //type: "linear",
      },
      xaxis: {
        //autorange: true,
        range: [-50, 90],
      },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: ['sendDataToCloud', 'lasso2d'],
    },
    data: [],
  };

  private subscribtions: any = {};

  public set ind(newindex: number) {
    if (newindex != -1) {
      this.index = newindex;
    }
  }

  public get ind() {
    return this.index;
  }

  set data(dataIn: any) {
    this.objektname = dataIn.name;
    this.stammid.set(dataIn.id);
    this.startpos = dataIn.start;
    this.rechte.set(dataIn.rechte);
  }

  setProfile(index: number) {
    const profileframes = this.profileframes();
    const curProfileData = profileframes[0].messpunkte[index];
    if (!curProfileData) return;

    this.currentIndex.set(index);
    this.currentPosition.set(curProfileData.Position);

    const sollprofile = this.sollprofile().find(p => p.name === curProfileData.ProfilL);

    this.pr.l = curProfileData.ProfilL;
    this.pr.r = curProfileData.ProfilR;

    if (!sollprofile) return;

    const idsString = this.selection_()
      .map(x => x.id)
      .join(',');

    this.apiService
      .getProfile(this.selectedIds(), curProfileData.Position.toString())
      .subscribe((profiles: Profil[]) => {
        if (!profiles) return;

        this.doRender(sollprofile.IstL, sollprofile.IstR, profiles, sollprofile.name, profileframes, sollprofile);
      });
  }

  doRender(
    lstSollL: Point[],
    lstSollR: Point[],
    profiles: any[],
    railTyp: string,
    profileframes: ProfileListItems[],
    sollprofile: SollProfile
  ) {
    var lstIstL = [];
    var lstIstR = [];

    for (let x = 0; x < profiles.length; x++) {
      const p = profiles[x];
      const frame = profileframes[x];

      if (!p?.istL || !p?.istR) continue;

      lstIstL.push({
        x: p.istL.map((k: { x: any }) => k.x),
        y: p.istL.map((k: { y: any }) => k.y),
        name: frame?.datum,
        type: 'scatter',
        mode: railTyp === '1' ? 'markers' : 'lines',
        marker: { size: 3 },
      });

      lstIstR.push({
        x: p.istR.map((k: { x: number }) => k.x),
        y: p.istR.map((k: { y: any }) => k.y),
        name: frame?.datum,
        type: 'scatter',
        mode: railTyp === '1' ? 'markers' : 'lines',
        marker: { size: 3 },
      });
    }

    this.graphL.data = [
      {
        x: lstSollL.map(p => p.x),
        y: lstSollL.map(p => p.y),
        name: sollprofile.name,
      },
    ].concat(lstIstL);

    this.graphR.data = [
      {
        x: lstSollR.map(p => p.x),
        y: lstSollR.map(p => p.y),
        name: sollprofile.name,
      },
    ].concat(lstIstR);

    if (this.graphL.id) {
      (this.graphL.layout as any).annotations = [];
      (this.graphR.layout as any).annotations = [];

      this.ds.lh = this.getdeltaH(lstSollL, profiles[0].istL);
      this.ds.rh = this.getdeltaH(lstSollR, profiles[0].istR);

      this.ds.l1 = this.getdeltaS1(profiles[0].istL, 'MaxL', 10);
      this.ds.r1 = this.getdeltaS1(profiles[0].istR, 'MaxR', 10);

      this.ds.l2 = this.getdeltaS2(profiles[0].istL, 'MaxL', 10);
      this.ds.r2 = this.getdeltaS2(profiles[0].istR, 'MaxR', 10);

      if (this.showdimensioning) {
        const messebene = 10;
        const curSoll_L = this.toXYArrays(lstSollL);
        const ist_L = this.toXYArrays(profiles[0].istL);
        let max_L = this.getHighestPoint(curSoll_L);

        const curSoll_R = this.toXYArrays(lstSollR);
        const ist_R = this.toXYArrays(profiles[0].istR);
        let max_R = this.getHighestPoint(curSoll_R);

        if (max_L) {
          const hvl0: Point = { x: max_L.x, y: max_L.y };
          const mminlhv0: Point = { x: max_L.x, y: 0 };
          let hvl1 = this.getIntersectionFromData(hvl0, mminlhv0, this.graphL.data[1]);
          let txtdhl = '';
          let txtds1l = '';
          let txtds2l = '';

          let sps1lsoll = null;
          let sps1list = null;
          let sps2lsoll = null;
          let sps2list = null;

          if (hvl1 != null) {
            let sp01: Point = { x: -40, y: hvl1.y - messebene };
            let sp02: Point = { x: 0, y: hvl1.y - messebene };
            sps1lsoll = this.getIntersectionFromData(sp01, sp02, curSoll_L);
            if (sps1lsoll != null) {
              sps1list = this.getIntersectionFromData(sp01, sp02, this.graphL.data[1]);
              if (sps1list != null) {
                if (sps1lsoll.x - sps1list.x > 0) {
                  txtds1l = (sps1lsoll.x - sps1list.x).toFixed(2);
                }
              }
            }

            sp01 = { x: 30, y: hvl1.y - messebene };
            sps2lsoll = this.getIntersectionFromData(sp01, sp02, curSoll_L);
            if (sps2lsoll != null) {
              sps2list = this.getIntersectionFromData(sp01, sp02, this.graphL.data[1]);
              if (sps2list != null) {
                if (sps2list.x - sps2lsoll.x > 0) {
                  txtds2l = (sps2list.x - sps2lsoll.x).toFixed(2);
                }
              }
            }
          }

          if (hvl0 != null && hvl1 != null) txtdhl = (hvl0.y - hvl1.y).toFixed(2);

          if (hvl1 != null) {
            var gft_l = {
              x: [-79, 39],
              y: [hvl1.y - messebene, hvl1.y - messebene],
              mode: 'lines+text',
              text: ['GFT', ''],
              name: 'GFT',
              textposition: 'top',
              line: {
                dash: 'dashdot',
                color: 'rgb(100, 100, 100)',
                width: 1,
              },
            };

            this.graphL.data.push(gft_l);
          }
          if (hvl0 != null && hvl1 != null) {
            (this.graphL.layout as any).annotations = [
              {
                // Arrow start point (tip)
                x: hvl0.x,
                y: hvl0.y,
                // Arrow end offset in pixels
                ax: 0,
                ay: -50,
                axref: 'pixel',
                ayref: 'pixel',
                text: txtdhl,
                showarrow: true,
                arrowhead: 3, // filled triangle ≈ ISO style
                arrowside: 'end',
                arrowsize: 1.5,
                arrowwidth: 1.5,
                arrowcolor: '#000',
                font: {
                  family: 'Arial, sans-serif', // ISO recommends simple upright font
                  size: 12,
                  color: '#000',
                },
              },
              {
                // Arrow start point (tip)
                x: hvl1!.x,
                y: hvl1!.y,
                // Arrow end offset in pixels
                ax: 0,
                ay: 25,
                axref: 'pixel',
                ayref: 'pixel',
                showarrow: true,
                arrowhead: 3, // filled triangle ≈ ISO style
                arrowside: 'end',
                arrowsize: 1.5,
                arrowwidth: 1.5,
                arrowcolor: '#000',
              },
            ];
          }
          if (txtds1l != '') {
            let an0 = {
              x: sps1list.x,
              y: sps1list.y,
              ax: -40,
              ay: 0,
              axref: 'pixel',
              ayref: 'pixel',
              text: txtds1l,
              showarrow: true,
              arrowhead: 3,
              arrowside: 'end',
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
              xanchor: 'center',
              yanchor: 'bottom', // text sits just above the shaft
              font: {
                family: 'Arial, sans-serif',
                size: 12,
                color: '#000',
              },
            };

            let an1 = {
              x: sps1lsoll.x,
              y: sps1lsoll.y,
              // Arrow end offset in pixels
              ax: 25,
              ay: 0,
              axref: 'pixel',
              ayref: 'pixel',
              showarrow: true,
              arrowhead: 3, // filled triangle ≈ ISO style
              arrowside: 'end',
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
            };

            (this.graphL.layout as any).annotations.push(an0);
            (this.graphL.layout as any).annotations.push(an1);
          }
          if (txtds2l != '') {
            let an0 = {
              x: sps2list.x,
              y: sps2list.y,
              ax: 40,
              ay: 0,
              axref: 'pixel',
              ayref: 'pixel',
              text: txtds2l,
              showarrow: true,
              arrowhead: 3,
              arrowside: 'end',
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
              xanchor: 'center',
              yanchor: 'bottom', // text sits just above the shaft
              font: {
                family: 'Arial, sans-serif',
                size: 12,
                color: '#000',
              },
            };

            let an1 = {
              x: sps2lsoll.x,
              y: sps2lsoll.y,
              // Arrow end offset in pixels
              ax: -25,
              ay: 0,
              axref: 'pixel',
              ayref: 'pixel',
              showarrow: true,
              arrowhead: 3, // filled triangle ≈ ISO style
              arrowside: 'end',
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
            };

            (this.graphL.layout as any).annotations.push(an0);
            (this.graphL.layout as any).annotations.push(an1);
          }
          if (sps1list != null && sps2list != null) {
            var hilfslinie1 = {
              x: [sps1list.x, sps1list.x],
              y: [sps1list.y, -20],
              mode: 'lines',
              name: 'hilfslinie1',
              line: {
                color: '#000',
                width: 1,
              },
            };
            this.graphL.data.push(hilfslinie1);

            var hilfslinie2 = {
              x: [sps2list.x, sps2list.x],
              y: [sps2list.y, -20],
              mode: 'lines',
              name: 'hilfslinie2',
              line: {
                color: '#000',
                width: 1,
              },
            };
            this.graphL.data.push(hilfslinie2);
            let txtbreite = (sps2list.x + sps1list.x * -1).toFixed(2);

            let midX = (sps2list.x + sps1list.x) / 2;

            // ── Left arrowhead (pointing left) ────────────────────────
            const arrowLeft = {
              x: sps1list.x, // tip at left hilfslinie
              y: -18,
              xref: 'x',
              yref: 'y',
              ax: midX + 0.5, // tail at center
              ay: -18,
              axref: 'x',
              ayref: 'y',
              text: '',
              showarrow: true,
              arrowhead: 3,
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
            };

            // ── Right arrowhead (pointing right) ──────────────────────
            const arrowRight = {
              x: sps2list.x, // tip at right hilfslinie
              y: -18,
              xref: 'x',
              yref: 'y',
              ax: midX, // tail at center
              ay: -18,
              axref: 'x',
              ayref: 'y',
              text: '',
              showarrow: true,
              arrowhead: 3,
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
            };

            // Text centered separately
            const textAn = {
              x: midX,
              y: -18,
              xref: 'x',
              yref: 'y',
              text: txtbreite,
              showarrow: false,
              xanchor: 'center',
              yanchor: 'bottom',
              yshift: 6,
              font: {
                family: 'Arial, sans-serif',
                size: 12,
                color: '#000',
              },
            };

            (this.graphL.layout as any).annotations.push(arrowLeft);
            (this.graphL.layout as any).annotations.push(arrowRight);
            (this.graphL.layout as any).annotations.push(textAn);
          }
        }

        if (max_R) {
          const hvr0: Point = { x: max_R.x, y: max_R.y };
          const mminrhv0: Point = { x: max_R.x, y: 0 };
          let hvr1 = this.getIntersectionFromData(hvr0, mminrhv0, this.graphR.data[1]);
          let txtdhr = '';
          let txtds1r = '';
          let txtds2r = '';

          let sps1rsoll = null;
          let sps1rist = null;
          let sps2rsoll = null;
          let sps2rist = null;

          if (hvr1 != null) {
            let sp01: Point = { x: 40, y: hvr1.y - messebene };
            const sp02: Point = { x: 0, y: hvr1.y - messebene };
            sps1rsoll = this.getIntersectionFromData(sp01, sp02, curSoll_R);
            if (sps1rsoll != null) {
              sps1rist = this.getIntersectionFromData(sp01, sp02, this.graphR.data[1]);
              if (sps1rist != null) {
                if (sps1rist.x - sps1rsoll.x > 0) {
                  txtds1r = (sps1rist.x - sps1rsoll.x).toFixed(2);
                }
              }
            }

            sp01 = { x: -30, y: hvr1.y - messebene };
            sps2rsoll = this.getIntersectionFromData(sp01, sp02, curSoll_R);
            if (sps2rsoll != null) {
              sps2rist = this.getIntersectionFromData(sp01, sp02, this.graphR.data[1]);
              if (sps2rist != null) {
                if (sps2rsoll.x - sps2rist.x > 0) {
                  txtds2r = (sps2rsoll.x - sps2rist.x).toFixed(2);
                }
              }
            }
          }

          if (hvr0 != null && hvr1 != null) txtdhr = (hvr0.y - hvr1.y).toFixed(2);

          if (hvr1 != null) {
            var gft_r = {
              x: [-39, 79],
              y: [hvr1.y - messebene, hvr1.y - messebene],
              mode: 'lines+text',
              name: 'GFT',
              text: ['', 'GFT'],
              textposition: 'top',
              line: {
                dash: 'dashdot',
                color: 'rgb(100, 100, 100)',
                width: 1,
              },
            };
            this.graphR.data.push(gft_r);
          }
          if (hvr0 != null && hvr1 != null) {
            (this.graphR.layout as any).annotations = [
              {
                // Arrow start point (tip)
                x: hvr0.x,
                y: hvr0.y,
                // Arrow end offset in pixels
                ax: 0,
                ay: -50,
                axref: 'pixel',
                ayref: 'pixel',
                text: txtdhr,
                showarrow: true,
                arrowhead: 3, // filled triangle ≈ ISO style
                arrowside: 'end',
                arrowsize: 1.5,
                arrowwidth: 1.5,
                arrowcolor: '#000',
                font: {
                  family: 'Arial, sans-serif', // ISO recommends simple upright font
                  size: 12,
                  color: '#000',
                },
              },
              {
                // Arrow start point (tip)
                x: hvr1!.x,
                y: hvr1!.y,
                // Arrow end offset in pixels
                ax: 0,
                ay: 25,
                axref: 'pixel',
                ayref: 'pixel',
                showarrow: true,
                arrowhead: 3, // filled triangle ≈ ISO style
                arrowside: 'end',
                arrowsize: 1.5,
                arrowwidth: 1.5,
                arrowcolor: '#000',
              },
            ];
          }
          if (txtds1r != '') {
            let an0 = {
              x: sps1rist.x,
              y: sps1rist.y,
              ax: 40,
              ay: 0,
              axref: 'pixel',
              ayref: 'pixel',
              text: txtds1r,
              showarrow: true,
              arrowhead: 3,
              arrowside: 'end',
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
              xanchor: 'center',
              yanchor: 'bottom', // text sits just above the shaft
              font: {
                family: 'Arial, sans-serif',
                size: 12,
                color: '#000',
              },
            };

            let an1 = {
              x: sps1rsoll.x,
              y: sps1rsoll.y,
              // Arrow end offset in pixels
              ax: -25,
              ay: 0,
              axref: 'pixel',
              ayref: 'pixel',
              showarrow: true,
              arrowhead: 3, // filled triangle ≈ ISO style
              arrowside: 'end',
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
            };

            (this.graphR.layout as any).annotations.push(an0);
            (this.graphR.layout as any).annotations.push(an1);
          }
          if (txtds2r != '') {
            let an0 = {
              x: sps2rist.x,
              y: sps2rist.y,
              ax: -40,
              ay: 0,
              axref: 'pixel',
              ayref: 'pixel',
              text: txtds2r,
              showarrow: true,
              arrowhead: 3,
              arrowside: 'end',
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
              xanchor: 'center',
              yanchor: 'bottom', // text sits just above the shaft
              font: {
                family: 'Arial, sans-serif',
                size: 12,
                color: '#000',
              },
            };

            let an1 = {
              x: sps2rsoll.x,
              y: sps2rsoll.y,
              // Arrow end offset in pixels
              ax: 25,
              ay: 0,
              axref: 'pixel',
              ayref: 'pixel',
              showarrow: true,
              arrowhead: 3, // filled triangle ≈ ISO style
              arrowside: 'end',
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
            };

            (this.graphR.layout as any).annotations.push(an0);
            (this.graphR.layout as any).annotations.push(an1);
          }
          if (sps1rist != null && sps2rist != null) {
            var hilfslinie1 = {
              x: [sps1rist.x, sps1rist.x],
              y: [sps1rist.y, -20],
              mode: 'lines',
              name: 'hilfslinie1',
              line: {
                color: '#000',
                width: 1,
              },
            };
            this.graphR.data.push(hilfslinie1);

            var hilfslinie2 = {
              x: [sps2rist.x, sps2rist.x],
              y: [sps2rist.y, -20],
              mode: 'lines',
              name: 'hilfslinie2',
              line: {
                color: '#000',
                width: 1,
              },
            };
            this.graphR.data.push(hilfslinie2);
            let txtbreite = (sps1rist.x + sps2rist.x * -1).toFixed(2);

            let midX = (sps2rist.x + sps1rist.x) / 2;

            // ── Left arrowhead (pointing left) ────────────────────────
            const arrowLeft = {
              x: sps1rist.x, // tip at left hilfslinie
              y: -18,
              xref: 'x',
              yref: 'y',
              ax: midX - 0.5, // tail at center
              ay: -18,
              axref: 'x',
              ayref: 'y',
              text: '',
              showarrow: true,
              arrowhead: 3,
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
            };

            // ── Right arrowhead (pointing right) ──────────────────────
            const arrowRight = {
              x: sps2rist.x, // tip at right hilfslinie
              y: -18,
              xref: 'x',
              yref: 'y',
              ax: midX, // tail at center
              ay: -18,
              axref: 'x',
              ayref: 'y',
              text: '',
              showarrow: true,
              arrowhead: 3,
              arrowsize: 1.5,
              arrowwidth: 1.5,
              arrowcolor: '#000',
            };

            // Text centered separately
            const textAn = {
              x: midX,
              y: -18,
              xref: 'x',
              yref: 'y',
              text: txtbreite,
              showarrow: false,
              xanchor: 'center',
              yanchor: 'bottom',
              yshift: 6,
              font: {
                family: 'Arial, sans-serif',
                size: 12,
                color: '#000',
              },
            };

            (this.graphR.layout as any).annotations.push(arrowLeft);
            (this.graphR.layout as any).annotations.push(arrowRight);
            (this.graphR.layout as any).annotations.push(textAn);
          }
        }
      }

      Plotly.react(this.graphL.id, this.graphL.data, this.graphL.layout);
      Plotly.react(this.graphR.id, this.graphR.data, this.graphR.layout);
    }
  }

  onSliderChange(value: number | string) {
    this.setProfile(Number(value));
  }

  selectionProfileChanged(event: ResultItem[]) {
    this.selection_.set(event);
    this.setProfile(this.currentIndex());
  }

  onDimensioningChange(value: boolean) {
    this.setProfile(this.currentIndex());
  }

  getPosition = (val: any) => {
    if (val !== undefined && !Number.isNaN(val)) {
      const frames = this.profileframes();
      const frame = frames[0].messpunkte[val];

      if (frame) {
        let m = frame.Position;
        this.pr.weg = +Number(m.toFixed(2));
        return +Number(m.toFixed(2));
      }
      return val;
    }
    return 0;
  };

  getHighestPoint(data: { x: number[]; y: number[] }) {
    if (!data.y.length) return null;

    const { x, y } = data;

    if (!x.length || !y.length || x.length !== y.length) return null;

    // Finde den maximalen Y-Wert
    const maxY = Math.max(...y);

    // Filtere alle Punkte mit diesem maxY
    const candidates = [];
    for (let i = 0; i < y.length; i++) {
      if (y[i] === maxY) {
        candidates.push({ x: x[i], y: y[i] });
      }
    }

    // Finde den Punkt, der am nächsten zu (0,0) liegt
    let closest = candidates[0];
    let minDistance = Math.sqrt(closest.x ** 2 + closest.y ** 2);

    for (const point of candidates) {
      const dist = Math.sqrt(point.x ** 2 + point.y ** 2);
      if (dist < minDistance) {
        closest = point;
        minDistance = dist;
      }
    }
    return closest;
  }

  getIntersectionFromData(p0: Point, p1: Point, data: { x: number[]; y: number[] }): Point | null {
    const { x, y } = data;
    if (x.length !== y.length || x.length < 2) return null;

    for (let i = 0; i < x.length - 1; i++) {
      const segmentStart: Point = { x: x[i], y: y[i] };
      const segmentEnd: Point = { x: x[i + 1], y: y[i + 1] };

      const intersection = this.getLineIntersection(p0, p1, segmentStart, segmentEnd);
      if (intersection) return intersection; // erster Schnittpunkt
    }
    return null;
  }

  // Schnittpunkt zweier Segmente (p0→p1) und (p2→p3)
  getLineIntersection(p0: Point, p1: Point, p2: Point, p3: Point): Point | null {
    const s1_x = p1.x - p0.x;
    const s1_y = p1.y - p0.y;
    const s2_x = p3.x - p2.x;
    const s2_y = p3.y - p2.y;

    const denom = -s2_x * s1_y + s1_x * s2_y;
    if (denom === 0) return null; // parallel oder überlappend

    const s = (-s1_y * (p0.x - p2.x) + s1_x * (p0.y - p2.y)) / denom;
    const t = (s2_x * (p0.y - p2.y) - s2_y * (p0.x - p2.x)) / denom;

    if (s >= 0 && s <= 1 && t >= 0 && t <= 1) {
      return {
        x: p0.x + t * s1_x,
        y: p0.y + t * s1_y,
      };
    }

    return null; // kein Schnittpunkt innerhalb der Segmente
  }

  getdeltaH(Soll: Point[], Ist: Point[]) {
    this.curSoll = this.toXYArrays(Soll);
    this.ist = this.toXYArrays(Ist);
    let max = this.getHighestPoint(this.curSoll);
    if (max) {
      try {
        this.hp01 = { x: max.x, y: max.y };
        this.hp02 = { x: max.x, y: 0 };
        this.hp0 = this.getIntersectionFromData(this.hp01, this.hp02, this.ist);
        if (this.hp0 != null) return (this.hp01.y - this.hp0.y).toFixed(2);
        else return '0.00';
      } catch (error) {
        console.error('Something went wrong!', error);
      }
    }
    return '0.00';
  }

  getdeltaS1(Ist: Point[], direction: string, messebene: number) {
    const ist = this.toXYArrays(Ist);
    if (this.hp0 != null) {
      this.sp01 = {
        x: direction == 'MaxL' ? -40 : 40,
        y: this.hp0.y - messebene,
      };
      this.sp02 = { x: 0, y: this.hp0.y - messebene };
      this.spSoll = this.getIntersectionFromData(this.sp01, this.sp02, this.curSoll);
      if (this.spSoll != null) {
        this.spIst = this.getIntersectionFromData(this.sp01, this.sp02, ist);
        if (this.spIst != null) {
          if (direction == 'MaxL') {
            if (this.spSoll.x - this.spIst.x > 0) {
              return (this.spSoll.x - this.spIst.x).toFixed(2);
            } else return '0.00';
          } else {
            if (this.spIst.x - this.spSoll.x > 0) {
              return (this.spIst.x - this.spSoll.x).toFixed(2);
            } else return '0.00';
          }
        } else return '0.00';
      } else return '0.00';
    } else return '0.00';
  }

  getdeltaS2(Ist: Point[], direction: string, messebene: number) {
    const ist = this.toXYArrays(Ist);
    if (this.hp0 != null) {
      this.sp01L = {
        x: direction == 'MaxL' ? 40 : -40,
        y: this.hp0.y - messebene,
      };
      this.sp02L = { x: 0, y: this.hp0.y - messebene };
      this.spSollL = this.getIntersectionFromData(this.sp01L, this.sp02L, this.curSoll);
      if (this.spSollL != null) {
        let spIst = this.getIntersectionFromData(this.sp01L, this.sp02L, ist);
        if (spIst != null) {
          if (direction == 'MaxL') {
            if (spIst.x - this.spSollL.x > 0) {
              return (spIst.x - this.spSollL.x).toFixed(2);
            } else return '0.00';
          } else {
            if (this.spSollL.x - spIst.x > 0) {
              return (this.spSollL.x - spIst.x).toFixed(2);
            } else return '0.00';
          }
        } else return '0.00';
      } else return '0.00';
    } else return '0.00';
  }

  toXYArrays(points: Point[]): { x: number[]; y: number[] } {
    const x: number[] = [];
    const y: number[] = [];

    for (const p of points) {
      x.push(p.x);
      y.push(p.y);
    }

    return { x, y };
  }

  doExcelExport() {
    this.excelService.exportAsExcelFile(this.exportData, this.mrTranslate.transform('Querprofil') + '-' + this.pr.weg);
  }

  getMesswerte() {
    const l: { x: number[]; y: number[] } = this.graphL.data[1];
    const r: { x: number[]; y: number[] } = this.graphR.data[1];
    let res: { lX: number; lY: number; rX: number; rY: number }[];
    if (l.x.length >= r.x.length)
      this.exportData = l.x.map((x, index) => ({
        lX: x,
        lY: l.y[index],
        rX: r.x[index],
        rY: r.y[index],
      }));
    else
      this.exportData = r.x.map((x, index) => ({
        rX: x,
        rY: r.y[index],
        lX: l.x[index],
        lY: l.y[index],
      }));

    this.showmesswerte = !this.showmesswerte;
  }

  increase() {
    if (this.ind + 0.5! <= this.slideroptions.ceil!) {
      this.ind += 0.5;
    }
  }

  decrease() {
    if (this.ind - 0.5 >= this.slideroptions.floor!) {
      this.ind -= 0.5;
    }
  }

  doMove(val: number, side: string, dir: string) {
    var graph: any;
    var soll = side == 'L' ? (graph = this.graphL) : (graph = this.graphR);

    if (dir == 'X') {
      graph.data[1].x = graph.data[1].x.map((xVal: any) => xVal + val);
    } else {
      graph.data[1].y = graph.data[1].y.map((xVal: number) => xVal - val);
    }

    Plotly.redraw(graph.id);

    const profileframes = this.profileframes();
    const curProfileData = profileframes[0].messpunkte[this.currentIndex()];
    if (!curProfileData) return;

    const sollprofile = this.sollprofile().find(p => p.name === curProfileData.ProfilL);

    if (!sollprofile) return;

    var preSoll = side == 'L' ? sollprofile.IstL : sollprofile.IstR;

    const trace = graph.data[1];
    const points: Point[] = trace.x.map((x: number, i: number) => ({
      x,
      y: trace.y[i],
    }));

    if (side == 'L') {
      this.ds.lh = this.getdeltaH(preSoll, points);
      this.ds.l1 = this.getdeltaS1(points, 'MaxL', 10);
      this.ds.l2 = this.getdeltaS2(points, 'MaxL', 10);
    } else {
      this.ds.rh = this.getdeltaH(preSoll, points);
      this.ds.r1 = this.getdeltaS1(points, 'MaxR', 10);
      this.ds.r2 = this.getdeltaS2(points, 'MaxR', 10);
    }
  }

  setMoveStep(i: any, mode: string) {
    var val = +i.currentTarget.value;
    if (mode == 'L') this.moveStepL = val;
    else this.moveStepR = val;
  }

  getHeightWearDia() {}

  openWearModal(diaData: any) {
    const fixedData = diaData.map((trace: any) => ({
      ...trace,
      type: 'scatter',
      mode: 'lines',
    }));

    this.graphWear.data = fixedData;

    this.showWearDia = true;

    // Wait until the modal DOM is visible
    setTimeout(() => {
      const div = this.plotDiv?.nativeElement;
      if (!div) return;

      Plotly.newPlot(div, this.graphWear.data, this.graphWear.layout, this.graphWear.config);
    }, 0);
  }

  saveProfile(mode: string) {
    var i = 0;
    var prof: { x: number[]; y: any[] };
    let profLine = '';
    if (mode == 'L') prof = this.graphL.data[1];
    else prof = this.graphR.data[1];

    const points: Point[] = prof.x.map((x: number, i: number) => ({
      x,
      y: prof.y[i],
    }));

    const profileframes = this.profileframes();
    const curProfileData = profileframes[0].messpunkte[this.currentIndex()];
    if (!curProfileData) return;

    var body = {
      id: profileframes[0].id,
      file: curProfileData.File,
      profiles: points,
      mode: mode,
      stammid: this.stammid,
      zIndex: this.ind,
      dS: this.ds,
    };
    this.apiservice
      .setAnlageProfil(body)
      .pipe()
      .subscribe((val: any) => {
        this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      });
    this.moveXL = this.moveXR = this.moveYL = this.moveYR = 0;
  }

  public getDistance(p1: Point, p2: Point) {
    var a = p1.x - p2.x;
    var b = p1.y - p2.y;

    return Math.sqrt(a * a + b * b);
  }

  public getDistanceNum(p1x: number, p2x: number, p1y: number, p2y: number) {
    var a = p1x - p2x;
    var b = p1y - p2y;

    return Math.sqrt(a * a + b * b).toFixed(2);
  }

  handleClick(data: { points: string | any[] }, side: string) {
    var graph: any;
    side == 'l' ? (graph = this.graphL) : (graph = this.graphR);
    graph.data = graph.data.filter((item: { name: string }) => item.name != 'Distanzmessung');
    if (data.points.length == 1) {
      if (!this.xy.x || (this.xy.x && this.xy.x2)) {
        this.xy.x = data.points[0].x;
        this.xy.y = data.points[0].y;
        this.xy.x2 = undefined;
        this.xy.y2 = undefined;
        var dataPoint = {
          x: [this.xy.x],
          y: [this.xy.y],
          name: 'Distanzmessung',
          type: 'lines+marker',
        };
        var txt = 'x=0.00 mm y=0.00 mm dist=0.00 mm';
        //Plotly.addTraces(graph.id,{ x: [this.xy.x], y: [this.xy.y], name: "Distanzmessung"})
      } else {
        this.xy.x2 = data.points[0].x;
        this.xy.y2 = data.points[0].y;
        var dist = this.getDistanceNum(this.xy.x, this.xy.x2, this.xy.y, this.xy.y2);
        var txt =
          'x=' +
          (this.xy.x - this.xy.x2).toFixed(2) +
          ' mm y=' +
          (this.xy.y - this.xy.y2).toFixed(2) +
          ' mm dist=' +
          dist +
          ' mm';
        var dataPoint = {
          x: [this.xy.x, this.xy.x2],
          y: [this.xy.y, this.xy.y2],
          name: 'Distanzmessung',
          type: 'lines+marker',
        };
        //Plotly.addTraces(graph.id,{ x: [this.xy.x, this.xy.x2], y: [this.xy.y, this.xy.y2], name: "Distanzmessung"})
      }

      graph.layout.annotations = [
        {
          text: txt,
          x: NaN, //Garantiert dass Annotation immer oben links zu sehen ist.
          y: NaN,
          showarrow: false,
          font: {
            family: 'Arial, sans-serif',
            size: 16,
            color: '#ff0000',
          },
        },
      ];

      graph.data.push(dataPoint);
      //Plotly.redraw(graph.id);
      Plotly.react(this.graphL.id, this.graphL.data, this.graphL.layout);
      Plotly.react(this.graphR.id, this.graphR.data, this.graphR.layout);
    }
  }

  initPlot(plotID: string, graph: any, side: string) {
    Plotly.react(plotID, graph.data, graph.layout).then((gd: any) => {
      gd.on('plotly_click', (data: any) => {
        this.handleClick(data, side);
      });
    });
  }

  ngAfterViewInit() {
    this.ctxL = this.canvasRefL?.nativeElement.getContext('2d');
    this.ctxL?.scale(this.GLOB_SCALE, this.GLOB_SCALE);

    this.ctxR = this.canvasRefR?.nativeElement.getContext('2d');
    this.ctxR?.scale(this.GLOB_SCALE, this.GLOB_SCALE);

    this.moveStepL = 1;
    this.moveStepR = 1;

    this.initPlot('plotly_Left', this.graphL, 'l');
    this.initPlot('plotly_Right', this.graphR, 'r');
  }

  ngOnDestroy(): void {
    this.subscribtions?.profiles?.unsubscribe();
    this.profiles = null;
    this.sollProfiles = null;
  }

  close() {
    this.closedialog.next(true);
    this.indexScrub.next(-1);
  }
}

export class Point {
  x: number | null;
  y: number | null;
  constructor() {
    this.x = null;
    this.y = null;
  }
}
